import React, { useRef, useState } from "react"
import {
  VStack,
  Text,
  HStack,
  Input,
  Box,
  Grid,
  Switch,
  useOutsideClick,
} from "@chakra-ui/react"
import { ModalName } from "sharedTypes"
import Button from "components/Buttons/Button"
import SegmentedControl from "components/SegmentedControl"
import useModal from "services/useModal"
import { PrinterMediaLabels, PrinterModelLabels } from "components/BadgeBuilder/utils/printers"
import EditorPane from "components/CanvasEditor/EditorPane"
import { useBadgeTemplate } from "../../hooks/useBadgeTemplate"
import { useBadgeUI } from "../../contexts/BadgeUIContext"
import { getTemplateDimensions } from "../../utils/badgeHelpers"
import { formatMeasurement } from "../../utils/units"
import DevControls from "./DevControls"
import DevOnly from "../../DevOnly"

const BadgeGeneralPanel = () => {
  const {
    currentTemplate,
    handleTemplatePropertyChange,
  } = useBadgeTemplate()

  const {
    displayUnit, setDisplayUnit, showGrid, setShowGrid, snapping, setSnapping,
  } = useBadgeUI()
  const showModal = useModal()
  const [isEditingName, setIsEditingName] = useState(false)
  const [tempName, setTempName] = useState("")
  const editTemplateNameRef = useRef<HTMLInputElement>(null)
  const editContainerRef = useRef<HTMLDivElement>(null)

  useOutsideClick({
    ref: editContainerRef,
    handler: () => {
      if (isEditingName) {
        handleCancelNameEdit()
      }
    },
  })

  if (!currentTemplate) {
    return (
      <VStack spacing={6} align="stretch">
        <Text fontSize="sm" color="gray.500" textAlign="center">
          No template selected
        </Text>
      </VStack>
    )
  }

  const templateDimensions = getTemplateDimensions(currentTemplate)

  const handleStartNameEdit = () => {
    setTempName(currentTemplate.name)
    setIsEditingName(true)
  }

  const handleSaveNameEdit = () => {
    if (tempName.trim() && tempName.trim() !== currentTemplate.name) {
      handleTemplatePropertyChange({
        name: tempName.trim(),
      })
    }
    setIsEditingName(false)
    setTempName("")
  }

  const handleCancelNameEdit = () => {
    setIsEditingName(false)
    setTempName("")
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSaveNameEdit()
    } else if (e.key === "Escape") {
      handleCancelNameEdit()
    }
  }

  return (
    <VStack spacing={6} align="stretch" py={2}>
      <EditorPane title="Badge Builder Settings">
        <VStack spacing={3} align="stretch">
          <Grid templateColumns="1fr 2fr" alignItems="center" gap={4} w="full" justifyItems="between">
            <Text fontSize="sm">Display Unit</Text>
            <SegmentedControl
              options={[
                { label: "mm", value: "mm" },
                { label: "Inches", value: "in" },
              ]}
              value={displayUnit}
              onChange={(value) => {
                if (value === "mm" || value === "in") {
                  setDisplayUnit(value)
                }
              }}
              justifySelf="end"
              size="sm"
            />
          </Grid>
          <Grid templateColumns="1fr 2fr" alignItems="center" gap={4} w="full" justifyItems="between">
            <Text fontSize="sm">Show Grid</Text>
            <Switch
              aria-label="Show Grid"
              isChecked={showGrid}
              onChange={(e) => setShowGrid(e.target.checked)}
              justifySelf="end"
            />
          </Grid>
          <Grid templateColumns="1fr 2fr" alignItems="center" gap={4} w="full" justifyItems="between">
            <Text fontSize="sm">Snapping</Text>
            <Switch
              aria-label="Snapping"
              isChecked={snapping}
              onChange={(e) => setSnapping(e.target.checked)}
              justifySelf="end"
            />
          </Grid>
        </VStack>
      </EditorPane>
      {/* Template Section */}
      <EditorPane title="Template">
        <VStack spacing={4} align="stretch">
          <Box>
            <Text fontSize="sm" fontWeight="bold" color="gray.800" mb={1}>
              Name
            </Text>
            {isEditingName ? (
              <HStack spacing={2} ref={editContainerRef}>
                <Input
                  ref={editTemplateNameRef}
                  value={tempName}
                  onChange={(e) => setTempName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  autoFocus
                  size="sm"
                  fontSize="sm"
                  placeholder="Template name"
                  bg="white"
                />
                <Button
                  size="sm"
                  colorScheme="gray"
                  onClick={handleCancelNameEdit}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="solid"
                  colorScheme="blue"
                  onClick={handleSaveNameEdit}
                >
                  Save
                </Button>
              </HStack>
            ) : (
              <HStack spacing={2} justify="space-between">
                <Box
                  as="button"
                  flex={1}
                  onClick={handleStartNameEdit}
                  py={1}
                  borderRadius="md"
                  textAlign="left"
                  noOfLines={1}
                >
                  <Text fontSize="sm" fontWeight="medium">
                    {currentTemplate.name}
                  </Text>
                </Box>
                <Button
                  aria-label="Edit template name"
                  variant="outline"
                  leftIcon="pencil"
                  size="sm"
                  onClick={handleStartNameEdit}
                >
                  Edit
                </Button>
              </HStack>
            )}
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="bold" color="gray.800" mb={1}>
              Printer & Media
            </Text>
            <HStack spacing={2} justify="space-between">
              <VStack spacing={0} align="start" flex={1}>
                <Text fontSize="sm" noOfLines={1}>
                  {PrinterModelLabels[currentTemplate.printerModel]}
                </Text>
                <Text fontSize="xs" color="gray.500" noOfLines={1}>
                  {PrinterMediaLabels[currentTemplate.printerMedia]}
                </Text>
              </VStack>
              <Button
                aria-label="Edit printer & media"
                leftIcon="pencil"
                variant="outline"
                size="sm"
                onClick={() => showModal(ModalName.EditBadgeTemplatePrinterAndMedia, {
                  template: currentTemplate,
                  onTemplateChange: handleTemplatePropertyChange,
                })}
              >
                Edit
              </Button>
            </HStack>
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="bold" color="gray.800" mb={1}>
              Dimensions
            </Text>
            <HStack spacing={2} justify="space-between">
              <VStack spacing={0} align="start" flex={1}>
                <Text fontSize="sm">
                  {formatMeasurement(templateDimensions.width, displayUnit)}
                  {" "}×{" "}
                  {formatMeasurement(templateDimensions.height, displayUnit)}
                </Text>
              </VStack>

              <Button
                aria-label="Edit template dimensions"
                variant="outline"
                leftIcon="pencil"
                size="sm"
                onClick={() => showModal(ModalName.EditBadgeTemplateDimensions, {
                  template: currentTemplate,
                  onTemplateChange: handleTemplatePropertyChange,
                })}
              >
                Edit
              </Button>
            </HStack>

          </Box>

        </VStack>
      </EditorPane>

      {/* Developer Controls */}
      <DevOnly>
        <>
          <EditorPane title="Preview">
            <VStack spacing={3} align="stretch">
              <Button
                variant="outline"
                size="sm"
                colorScheme="blue"
                isDisabled
              >
                Export to PDF
              </Button>
            </VStack>
          </EditorPane>

          <DevControls template={currentTemplate} />
        </>
      </DevOnly>
    </VStack>
  )
}

export default BadgeGeneralPanel
